import React from 'react';
import {
  Box, Typography, TextField, Button, Paper, Grid, Avatar, Snackbar, Alert, CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import { PageHeader, FieldRow, SaveBar, ErrorState, PermissionState } from '../ui';
import useMyCompanyPage from './useMyCompanyPage';

function MyCompany() {
  const p = useMyCompanyPage();

  if (p.status === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (p.status === 'error') {
    return (
      <ErrorState
        title={p.t("Couldn't load company data", { defaultValue: "Couldn't load company data" })}
        description={p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' })}
        request={p.errorInfo?.request}
        retryLabel={p.t('Try again', { defaultValue: 'Try again' })}
        onRetry={p.fetchCompany}
      />
    );
  }

  if (p.status === 'permission') {
    return (
      <PermissionState
        title={p.t("You don't have access to My Company", { defaultValue: "You don't have access to My Company" })}
        description={p.t('Ask your workspace owner for access.', { defaultValue: 'Ask your workspace owner for access.' })}
      />
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <PageHeader title={p.t('My Company', { defaultValue: 'My Company' })} subtitle={p.t('Your business details, shown on invoices and shipping labels', { defaultValue: 'Your business details, shown on invoices and shipping labels' })} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
            <Typography variant="h6" gutterBottom>{p.t('Company Logo')}</Typography>

            <Box sx={{ mb: 2, mt: 2 }}>
              <Avatar
                src={p.previewUrl || p.companyLogo || undefined}
                alt={p.form.companyName}
                sx={{ width: 150, height: 150, border: '1px solid', borderColor: 'divider' }}
              />
            </Box>

            <input accept="image/*" style={{ display: 'none' }} id="logo-upload-button" type="file" onChange={p.handleFileChange} />
            <label htmlFor="logo-upload-button">
              <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} sx={{ mb: 2 }}>
                {p.t('Select Image')}
              </Button>
            </label>

            <Button
              variant="contained"
              onClick={p.handleLogoUpload}
              disabled={!p.selectedFile || p.logoLoading}
              startIcon={p.logoLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {p.t('Upload Logo')}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>{p.t('Company Information')}</Typography>

            <FieldRow label={p.t('Company Name')}>
              <TextField
                fullWidth
                value={p.form.companyName}
                onChange={(e) => p.setForm({ ...p.form, companyName: e.target.value })}
              />
            </FieldRow>

            <FieldRow label={p.t('Support Email')}>
              <TextField
                fullWidth
                value={p.form.companyEmail}
                onChange={(e) => p.setForm({ ...p.form, companyEmail: e.target.value })}
                placeholder={p.t('e.g., support@example.com')}
              />
            </FieldRow>

            <FieldRow label={p.t('Support Phone')}>
              <TextField
                fullWidth
                value={p.form.companyPhone}
                onChange={(e) => p.setForm({ ...p.form, companyPhone: e.target.value })}
                placeholder={p.t('e.g., +1-234-567-8900')}
              />
            </FieldRow>

            <FieldRow label={p.t('Company Address')}>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={p.form.companyAddress}
                onChange={(e) => p.setForm({ ...p.form, companyAddress: e.target.value })}
              />
            </FieldRow>
          </Paper>
        </Grid>
      </Grid>

      <SaveBar
        dirty={p.isDirty}
        saving={p.saving}
        message={p.t('You have unsaved changes', { defaultValue: 'You have unsaved changes' })}
        discardLabel={p.t('Discard', { defaultValue: 'Discard' })}
        saveLabel={p.saving ? p.t('Saving...') : p.t('Save Changes')}
        onDiscard={p.discardChanges}
        onSave={p.handleSave}
      />

      <Snackbar
        open={p.snackbar.open}
        autoHideDuration={4000}
        onClose={() => p.setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={p.snackbar.severity} sx={{ width: '100%' }}>
          {p.snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default MyCompany;
